import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BOOK_PLANS } from '@kitup/book-schema';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router';
import { userErrorMessage } from '../api/errors.js';
import type { BookDto } from '../api/books.js';
import { exportsApi, type ExportDto, type ExportKind } from '../api/exports.js';
import { publicationApi, type PublicationDto } from '../api/publication.js';
import { EmptyState } from '../components/EmptyState.js';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';

const KINDS = ['pdf_print', 'pdf_screen'] as const satisfies readonly ExportKind[];

export function ExportStep({ book }: { readonly book: BookDto }): JSX.Element {
  const { t, formatDate, locale } = useI18n();
  const queryClient = useQueryClient();
  const printReady = BOOK_PLANS[book.plan].printReady;
  const kinds = printReady ? KINDS : (['pdf_screen'] as const);
  const [kind, setKind] = useState<ExportKind>(printReady ? 'pdf_print' : 'pdf_screen');
  const [reused, setReused] = useState(false);
  const exportsQuery = useQuery({
    queryKey: ['book-exports', book.id],
    queryFn: () => exportsApi.list(book.id),
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => item.status === 'queued' || item.status === 'running')
        ? 1_000
        : false,
  });
  const create = useMutation({
    mutationFn: (format: typeof kind = kind) => exportsApi.create(book.id, format),
    onSuccess: (item) => {
      setReused(item.cached);
      queryClient.setQueryData<{ items: readonly ExportDto[] }>(
        ['book-exports', book.id],
        (current) => ({
          items: [item, ...(current?.items ?? []).filter((existing) => existing.id !== item.id)],
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ['book-exports', book.id] });
    },
  });

  return (
    <section>
      <h1 className="text-2xl font-semibold">{t.export.title}</h1>
      <PublicationPanel bookId={book.id} bookTitle={book.coverTitle ?? book.title} />

      <fieldset className="mt-7">
        <legend className="mb-3 font-semibold">{t.export.formatTitle}</legend>
        <div className="grid gap-3 md:grid-cols-2">
          {kinds.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={kind === item}
              className="kitup-choice-card items-start text-left"
              onClick={() => {
                setKind(item);
                setReused(false);
              }}
            >
              <span className="font-semibold">{t.export.formats[item].title}</span>
              <span className="text-xs text-[var(--kitup-color-muted)]">
                {t.export.formats[item].description}
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="kitup-primary-button"
          disabled={create.isPending}
          onClick={() => create.mutate(kind)}
        >
          {create.isPending ? t.export.submitting : t.export.create}
        </button>
        {reused ? (
          <p role="status" className="text-sm text-emerald-700">
            {t.export.cached}
          </p>
        ) : null}
        {create.isError ? (
          <p role="alert" className="text-sm text-red-700">
            {t.export.failed}
          </p>
        ) : null}
      </div>

      <h2 className="mt-9 text-lg font-semibold">{t.export.history}</h2>
      {exportsQuery.isPending ? <p className="mt-3 text-sm">{t.common.loading}</p> : null}
      {exportsQuery.isError ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {t.export.failed}
        </p>
      ) : null}
      {exportsQuery.data?.items.length === 0 ? (
        <div className="mt-3">
          <EmptyState tone="inline" title={t.export.empty} hint={t.export.emptyHint} />
        </div>
      ) : null}
      <ol className="mt-3 grid gap-3">
        {exportsQuery.data?.items.map((item) => (
          <li key={item.id} className="rounded-2xl border border-[var(--kitup-color-border)] p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">{t.export.formats[item.kind].title}</p>
                <p className="text-xs text-[var(--kitup-color-muted)]">
                  {formatDate(item.createdAt)} · {t.export.status[item.status]}
                </p>
              </div>
              {item.downloadUrl ? (
                <a className="kitup-secondary-button" href={item.downloadUrl} download>
                  {t.export.download}
                </a>
              ) : null}
            </div>
            {item.status === 'queued' || item.status === 'running' ? (
              <div
                className="mt-3"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={item.progress}
              >
                <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full bg-[var(--kitup-color-primary)]"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                  {t.export.progress(item.progress)}
                </p>
              </div>
            ) : null}
            {/*
              The renderer's own message ("fetch failed") is for the logs, not a parent:
              say it didn't work, and offer the same file again.
            */}
            {item.error ? (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p role="alert" className="text-sm text-red-700">
                  {t.export.failed}
                </p>
                {/* Only on the newest of its format: an older failure was already redone. */}
                {exportsQuery.data?.items.find((other) => other.kind === item.kind)?.id ===
                item.id ? (
                  <button
                    type="button"
                    className="kitup-secondary-button"
                    disabled={create.isPending}
                    onClick={() => create.mutate(item.kind)}
                  >
                    {t.export.retry}
                  </button>
                ) : null}
              </div>
            ) : null}
            {item.downloadUrlExpiresAt ? (
              <p className="mt-2 text-xs text-[var(--kitup-color-muted)]">
                {/* The link lasts minutes; a date alone read as the rest of the day. */}
                {t.export.linkExpires(
                  new Date(item.downloadUrlExpiresAt).toLocaleTimeString(locale, {
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                )}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Публикация книги (§3.16). Живёт на шаге «Экспорт» рядом с PDF: и то и другое —
 * способ отдать готовую сказку наружу.
 *
 * Ссылка у книги одна и та же навсегда: снятие с публикации её глушит,
 * повторная публикация — включает обратно.
 */
function PublicationPanel({
  bookId,
  bookTitle,
}: {
  readonly bookId: string;
  readonly bookTitle: string;
}): JSX.Element {
  const { t, formatDate } = useI18n();
  const legal = useLegal();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  /**
   * Согласие на распространение (ст. 10.1 152-ФЗ). Всегда начинается снятым и
   * сбрасывается после публикации: заранее проставленный флажок согласием не
   * считается, а следующая публикация — это новое решение о новом составе книги.
   */
  const [consent, setConsent] = useState(false);
  const publication = useQuery({
    queryKey: ['book-publication', bookId],
    queryFn: () => publicationApi.get(bookId),
  });

  const applied = (next: PublicationDto): void => {
    setCopied(false);
    setShared(false);
    setQrOpen(false);
    setConsent(false);
    queryClient.setQueryData(['book-publication', bookId], next);
    // Запрет на правки читается из книги — её надо перезапросить (§3.16).
    void queryClient.invalidateQueries({ queryKey: ['book', bookId] });
    void queryClient.invalidateQueries({ queryKey: ['books'] });
  };
  const publish = useMutation({
    mutationFn: () => publicationApi.publish(bookId),
    onSuccess: applied,
  });
  const unpublish = useMutation({
    mutationFn: () => publicationApi.unpublish(bookId),
    onSuccess: applied,
  });

  const state = publication.data;
  const busy = publish.isPending || unpublish.isPending;
  const error = publish.error ?? unpublish.error;
  const canShare = typeof navigator.share === 'function';

  const copyLink = async (url: string): Promise<void> => {
    if (!navigator.clipboard) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setShared(false);
  };

  const shareLink = async (url: string): Promise<void> => {
    try {
      await navigator.share({ title: bookTitle, text: t.publish.shareText(bookTitle), url });
      setShared(true);
      setCopied(false);
    } catch (shareError: unknown) {
      // Закрытие системного меню — нормальный исход, а не ошибка публикации.
      if (shareError instanceof DOMException && shareError.name === 'AbortError') return;
      await copyLink(url);
    }
  };

  return (
    <section className="mt-7 rounded-2xl border border-[var(--kitup-color-border)] p-4">
      <h2 className="text-lg font-semibold">{t.publish.title}</h2>
      {/* How to get a link, until there is one. */}
      {state?.published ? null : (
        <p className="mt-1 max-w-prose text-sm text-[var(--kitup-color-muted)]">
          {t.publish.subtitle}
        </p>
      )}

      {publication.isPending ? <p className="mt-3 text-sm">{t.common.loading}</p> : null}

      {state ? (
        <>
          {state.published ? null : (
            <div className="mt-4 rounded-[var(--kitup-radius-sm)] bg-[var(--kitup-color-accent)]/5 p-3">
              <label className="flex items-start gap-3 text-sm font-medium">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={consent}
                  disabled={busy}
                  onChange={(event) => setConsent(event.target.checked)}
                />
                <span>
                  {t.publish.consent.label}{' '}
                  <Link className="underline" to={legal.routes.publicationConsent} target="_blank">
                    {t.publish.consent.documentLink}
                  </Link>
                  <span className="mt-1 block text-xs font-normal text-[var(--kitup-color-muted)]">
                    {t.publish.consent.hint} {t.publish.locksEditing}
                  </span>
                </span>
              </label>
            </div>
          )}

          {/*
            Published, the card leads with the link and its sharing; taking it down is the
            rare, destructive act, so it comes last and quietly instead of first.
          */}
          {state.published ? null : (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="kitup-primary-button"
                disabled={busy || !consent}
                onClick={() => publish.mutate()}
              >
                {publish.isPending ? t.publish.publishing : t.publish.publish}
              </button>
            </div>
          )}

          {state.published && state.url ? (
            <div className="mt-4 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <span
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-600 text-xl text-white"
                  aria-hidden="true"
                >
                  ✓
                </span>
                <div>
                  <h3 className="font-semibold text-emerald-950">{t.publish.readyTitle}</h3>
                  <p className="mt-1 max-w-prose text-sm text-emerald-900/75">
                    {t.publish.readyText}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {canShare ? (
                  <button
                    type="button"
                    className="kitup-primary-button"
                    onClick={() => void shareLink(state.url!)}
                  >
                    <ShareIcon />
                    {t.publish.share}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={canShare ? 'kitup-secondary-button' : 'kitup-primary-button'}
                  onClick={() => void copyLink(state.url!)}
                >
                  <LinkIcon />
                  {t.publish.copy}
                </button>
                <button
                  type="button"
                  className="kitup-secondary-button"
                  onClick={() => setQrOpen(true)}
                >
                  <QrIcon />
                  {t.publish.showQr}
                </button>
                <a
                  className="kitup-secondary-button"
                  href={state.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t.publish.open}
                </a>
              </div>

              {copied || shared ? (
                <p role="status" className="mt-3 text-sm font-medium text-emerald-700">
                  {shared ? t.publish.shared : t.publish.copied}
                </p>
              ) : null}

              <label className="mt-4 grid gap-1.5 border-t border-emerald-200/70 pt-3 text-sm font-medium">
                {t.publish.linkLabel}
                <input
                  className="kitup-input"
                  readOnly
                  value={state.url}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </label>

              <p className="mt-3 max-w-prose text-sm text-[var(--kitup-color-muted)]">
                {t.publish.stableLinkHint}
              </p>
            </div>
          ) : (
            <p className="mt-3 max-w-prose text-sm text-[var(--kitup-color-muted)]">
              {state.url ? t.publish.stableLinkHint : t.publish.unpublishedHint}
            </p>
          )}
        </>
      ) : null}

      {state?.published ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          {state.publishedAt ? (
            <p role="status" className="text-[var(--kitup-color-muted)]">
              {t.publish.published(formatDate(state.publishedAt))}
            </p>
          ) : null}
          <button
            type="button"
            className="kitup-quiet-button"
            disabled={busy}
            onClick={() => unpublish.mutate()}
          >
            {unpublish.isPending ? t.publish.unpublishing : t.publish.unpublish}
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {userErrorMessage(error, t)}
        </p>
      ) : null}

      {qrOpen && state?.url ? (
        <QrCardDialog
          bookTitle={bookTitle}
          publicUrl={state.url}
          onClose={() => setQrOpen(false)}
        />
      ) : null}
    </section>
  );
}

function QrCardDialog({
  bookTitle,
  publicUrl,
  onClose,
}: {
  readonly bookTitle: string;
  readonly publicUrl: string;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { name: brandName } = useBrand();
  const closeRef = useRef<HTMLButtonElement>(null);
  const qrRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-slate-950/55 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="publication-qr-title"
        className="kitup-card w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--kitup-color-primary)]">
              {t.publish.qrEyebrow}
            </p>
            <h2 id="publication-qr-title" className="mt-1 text-2xl font-semibold">
              {t.publish.qrTitle}
            </h2>
          </div>
          <button ref={closeRef} type="button" className="kitup-quiet-button" onClick={onClose}>
            {t.common.close}
          </button>
        </div>

        <div className="px-5 pb-5 pt-4">
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.publish.qrDescription}</p>
          <div className="relative mx-auto mt-5 grid max-w-[19rem] justify-items-center overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#fff4dc] via-white to-[#ece4ff] p-6 text-center shadow-inner">
            <span className="absolute left-5 top-4 text-amber-400" aria-hidden="true">
              ✦
            </span>
            <span className="absolute right-6 top-8 text-violet-300" aria-hidden="true">
              ●
            </span>
            <p className="max-w-[15rem] text-lg font-semibold leading-tight text-[var(--kitup-color-ink)]">
              {bookTitle}
            </p>
            <div className="mt-4 rounded-3xl bg-white p-2 shadow-lg ring-1 ring-black/5">
              <QRCodeSVG
                ref={qrRef}
                value={publicUrl}
                size={224}
                level="Q"
                marginSize={4}
                bgColor="#ffffff"
                fgColor="#30243a"
                title={t.publish.qrAlt(bookTitle)}
              />
            </div>
            <p className="mt-4 max-w-[15rem] text-sm font-medium leading-snug text-[var(--kitup-color-ink)]">
              {t.publish.qrInstruction}
            </p>
            <p className="mt-2 text-xs font-semibold tracking-wide text-[var(--kitup-color-primary)]">
              {brandName}
            </p>
          </div>

          <button
            type="button"
            className="kitup-primary-button mt-5 w-full justify-center"
            onClick={() =>
              downloadQrCard(
                qrRef.current,
                bookTitle,
                t.publish.qrEyebrow,
                t.publish.qrInstruction,
                brandName,
              )
            }
          >
            {t.publish.downloadQr}
          </button>
          <p className="mt-3 text-center text-xs text-[var(--kitup-color-muted)]">
            {t.publish.qrStableHint}
          </p>
        </div>
      </section>
    </div>
  );
}

function downloadQrCard(
  qr: SVGSVGElement | null,
  bookTitle: string,
  eyebrow: string,
  instruction: string,
  brandName: string,
): void {
  if (!qr) return;
  const qrMarkup = new XMLSerializer().serializeToString(qr);
  const titleLines = wrapTitle(bookTitle, 30)
    .map((line, index) => `<tspan x="540" y="${230 + index * 58}">${escapeXml(line)}</tspan>`)
    .join('');
  const hint = escapeXml(instruction);
  const cardEyebrow = escapeXml(eyebrow.toLocaleUpperCase());
  const brand = escapeXml(brandName);
  const qrViewBox = escapeXml(qr.getAttribute('viewBox') ?? '0 0 224 224');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350">
  <defs>
    <linearGradient id="paper" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#fff4dc"/><stop offset="0.52" stop-color="#fffdf8"/><stop offset="1" stop-color="#ece4ff"/>
    </linearGradient>
    <filter id="shadow"><feDropShadow dx="0" dy="18" stdDeviation="24" flood-opacity="0.13"/></filter>
  </defs>
  <rect width="1080" height="1350" rx="72" fill="url(#paper)"/>
  <circle cx="112" cy="120" r="13" fill="#e7b64c"/><circle cx="955" cy="160" r="20" fill="#c8b6e8"/>
  <path d="M150 220l10 25 25 10-25 10-10 25-10-25-25-10 25-10z" fill="#e7b64c"/>
  <text x="540" y="164" text-anchor="middle" fill="#76508f" font-family="Arial, sans-serif" font-size="30" font-weight="700" letter-spacing="5">${cardEyebrow}</text>
  <text text-anchor="middle" fill="#30243a" font-family="Georgia, serif" font-size="50" font-weight="700">${titleLines}</text>
  <rect x="178" y="330" width="724" height="724" rx="64" fill="#fff" filter="url(#shadow)"/>
  <svg x="202" y="354" width="676" height="676" viewBox="${qrViewBox}">${extractSvgContents(qrMarkup)}</svg>
  <text x="540" y="1145" text-anchor="middle" fill="#30243a" font-family="Arial, sans-serif" font-size="34" font-weight="600">${hint}</text>
  <text x="540" y="1234" text-anchor="middle" fill="#76508f" font-family="Arial, sans-serif" font-size="31" font-weight="700" letter-spacing="3">${brand}</text>
</svg>`;
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeFilename(bookTitle)}-qr.svg`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function extractSvgContents(svg: string): string {
  return svg.replace(/^<svg[^>]*>/u, '').replace(/<\/svg>$/u, '');
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function wrapTitle(value: string, lineLength: number): readonly string[] {
  const words = value.trim().split(/\s+/u);
  const lines: string[] = [];
  for (const word of words) {
    const index = Math.min(lines.length, 1);
    const current = lines[index] ?? '';
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= lineLength || !current) {
      lines[index] = candidate;
    } else if (index === 0) {
      lines.push(word);
    } else {
      lines[index] = `${current.slice(0, lineLength - 1).trimEnd()}…`;
      break;
    }
  }
  return lines.length > 0 ? lines : ['Book'];
}

function safeFilename(value: string): string {
  const safe = value
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-|-$/gu, '');
  return safe || 'book';
}

function ShareIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="mr-2 h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4" />
    </svg>
  );
}

function LinkIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="mr-2 h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.1 1.1" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.1-1.1" />
    </svg>
  );
}

function QrIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="mr-2 h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="3" width="6" height="6" />
      <rect x="15" y="3" width="6" height="6" />
      <rect x="3" y="15" width="6" height="6" />
      <path d="M15 15h2v2h-2zM19 15h2v6h-6v-2M15 11v2M11 15h2v6M19 11h2v2" />
    </svg>
  );
}
